import {
  StableUuidSchema,
  OpenProjectScopedRefreshResultSchema,
  type OpenProjectScopedRefreshResult,
  type OpenProjectAccountStatus,
  type StableUuid,
} from "@payday/contracts";

import {
  api as paydayApi,
  ApiRequestError,
  request,
  type Json,
} from "../../api.js";
import type {
  OpenProjectBalanceAdjustmentPreviewResponse,
  OpenProjectBalanceState,
  OpenProjectPayslipList,
  OpenProjectPeriodSummary,
  OpenProjectPohodaExportHistory,
  OpenProjectPohodaPreview,
  OpenProjectPayrollAccess,
  OpenProjectPayrollValueCorrectionInspection,
  OpenProjectPayrollValueCorrectionMutationResponse,
  OpenProjectYearSummary,
} from "../types/openproject.js";

export class PayrollApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly correlationId: string | undefined;
  public readonly retryable: boolean;

  public constructor(error: ApiRequestError) {
    super(error.message);
    this.name = "PayrollApiError";
    this.status = error.status;
    this.code = error.code ?? "payroll_request_failed";
    this.correlationId = error.correlationId;
    this.retryable = error.retryable ?? false;
  }
}

type DownloadableFile = {
  blob: Blob;
  fileName: string;
  contentType?: string;
  checksum?: string;
  exportId?: number;
  versionNumber?: number;
};

// ---------------------------------------------------------------------------
// Local response types.
//
// The legacy UI endpoints (the copied TAPP payroll routes and the
// `/payroll/legacy-ui/...` reads) return NAKED payloads. @payday/contracts
// models the real API with `{data, meta}` envelopes and stricter field shapes
// (e.g. `PayrollWorkProfileMutation`, `LockPayrollTimesheet`), so those
// contracts types cannot type these legacy-UI request/response forms directly.
// We therefore keep the shapes that the UI actually consumes here, and only
// use contracts types where they genuinely match (`StableUuid` for the stable
// employee identity). The richer legacy response shapes that already have
// schema-derived types are reused from `../types/openproject.ts`.
// ---------------------------------------------------------------------------

/** One work-profile row returned by `/payroll/legacy-ui/profiles`. */
export interface PayrollWorkProfileRow {
  id: string;
  privateEmployeeId: number;
  revision: number;
  sourceAccountStatus?: OpenProjectAccountStatus | null;
  displayName?: string;
  username?: string;
  payrollEmployeeCode?: string;
  payrollEmail?: string;
  payrollPdfPasswordConfigured?: boolean;
  payrollCalculationEnabled?: boolean;
  payrollEligibleForMonth?: boolean;
  payrollContractType?: string;
  payrollWorkloadPercent?: number;
  payrollHoursPerDay?: number;
  payrollAnnualHolidayEntitlement?: number;
  payrollStartDate?: string;
  payrollEndDate?: string;
  payrollProfileNotes?: string;
  [key: string]: unknown;
}

export interface OpenProjectPayrollProfilesResponse {
  users: PayrollWorkProfileRow[];
}

export interface PayrollProfilePeriodRow {
  id: number;
  userId: number | string;
  validToMonth: string;
  [key: string]: unknown;
}

export interface OpenProjectPayrollProfilePeriodsResponse {
  periods: PayrollProfilePeriodRow[];
}

/** Audit query accepted by `getOpenProjectPayrollAudit`. */
export type PayrollAuditLevel = "business" | "changes" | "technical";

export interface PayrollAuditQuery {
  level?: PayrollAuditLevel;
  /** Legacy TAPP flag that maps to the `technical` level. */
  technical?: boolean;
  month?: string;
  userId?: number | string;
  fromMonth?: string;
  throughMonth?: string;
  fieldKey?: string;
  eventKind?: string;
  scope?: string;
}

/**
 * One audit event. Declared locally (rather than reusing
 * `OpenProjectPayrollAuditEvent` from `../types/openproject.ts`) because the
 * `z.infer` optional fields there widen to `T | undefined`, which is not
 * assignable under `exactOptionalPropertyTypes` to the consumer's event shape.
 */
export interface PayrollAuditEvent {
  id: number;
  actorDisplayName: string;
  action: string;
  reason: string;
  targetType?: string;
  targetIdentifier?: string;
  beforePayload?: Record<string, unknown>;
  afterPayload?: Record<string, unknown>;
  context?: Record<string, unknown>;
  createdAt: string;
}

export interface OpenProjectPayrollAuditResponse {
  level: PayrollAuditLevel;
  filters: Partial<PayrollAuditQuery>;
  events: PayrollAuditEvent[];
}

/**
 * Mutation metadata merged into control-API mutation payloads. Mirrors
 * @payday/contracts `PayrollMutationMetadata`, but `expectedRevision` is
 * nullable here because the legacy vacation-opening-balance flow sends `null`.
 * Kept as a `type` (not `interface`) so it stays assignable to `Json`.
 */
export type PayrollMetadata = {
  expectedAuthorizationRevision: number;
  expectedRevision: number | null;
  reason: string;
  idempotencyKey: string;
};

export interface PayslipPreviewFile {
  fileName: string;
  blob: Blob;
}

export interface DownloadResult {
  fileName: string;
}

export interface BulkLockResult {
  month: string;
  unlocked: number;
  attempted: number;
  locked: number;
  skipped: number;
  failed: number;
  results: Array<{
    userId: number | string;
    name: string;
    locked: boolean;
    skipped: boolean;
    error: string;
  }>;
}

/** Concrete, typed Payroll API client (replaces the legacy untyped facade). */
export interface PayrollApi {
  getOpenProjectPayrollAccess(): Promise<OpenProjectPayrollAccess>;
  scopedRefresh(
    scope: "payroll" | "work_profiles",
    reason: string,
  ): Promise<OpenProjectScopedRefreshResult>;
  getOpenProjectPayrollProfiles(
    month?: string,
  ): Promise<OpenProjectPayrollProfilesResponse>;
  updateOpenProjectPayrollProfile(id: unknown, payload: Json): Promise<Json>;
  saveOpenProjectVacationOpeningBalance(
    id: unknown,
    payload: Json,
  ): Promise<Json>;
  getOpenProjectPayrollProfilePeriods(
    id: unknown,
  ): Promise<OpenProjectPayrollProfilePeriodsResponse>;
  createOpenProjectPayrollProfilePeriod(
    id: unknown,
    payload: Json,
  ): Promise<Json>;
  updateOpenProjectPayrollProfilePeriod(
    id: unknown,
    periodId: number,
    payload: Json,
  ): Promise<Json>;
  deleteOpenProjectPayrollProfilePeriod(
    id: unknown,
    periodId: number,
    reason: string,
  ): Promise<Json>;
  getOpenProjectPayrollWorkflow(month: string): Promise<Json>;
  getOpenProjectPayslips(month: string): Promise<OpenProjectPayslipList>;
  getOpenProjectPayslip(id: number): Promise<Json>;
  getOpenProjectBalances(month: string): Promise<OpenProjectBalanceState>;
  getOpenProjectPohodaPreview(month: string): Promise<OpenProjectPohodaPreview>;
  getOpenProjectPohodaExports(
    month: string,
  ): Promise<OpenProjectPohodaExportHistory>;
  getOpenProjectPayrollAudit(
    month?: string | PayrollAuditQuery,
  ): Promise<OpenProjectPayrollAuditResponse>;
  getOpenProjectMonthChangeLogs(month: string): Promise<Json>;
  getOpenProjectPeriodSummary(
    fromMonth: string,
    throughMonth: string,
  ): Promise<OpenProjectPeriodSummary>;
  getOpenProjectYearSummary(
    year: number,
    throughMonth: string,
  ): Promise<OpenProjectYearSummary>;
  uploadOpenProjectPayslips(month: string, files: File[]): Promise<Json>;
  calculateOpenProjectBalanceRow(month: string, employeeId: string, expectedRunId: number, expectedRunRevision: number): Promise<Json>;
  calculateOpenProjectBalances(month: string): Promise<Json>;
  approveOpenProjectBalanceRun(
    id: number,
    revision: number,
    reason?: string,
  ): Promise<Json>;
  confirmReadyOpenProjectPayslips(
    month: string,
    reason?: string,
  ): Promise<Json>;
  deleteOpenProjectPayslip(
    id: number,
    reason?: string,
    expectedRevision?: number,
    wrongMonthCleanup?: string,
  ): Promise<Json>;
  keepExistingOpenProjectPayslipsForBatch(
    id: number,
    payload: Json,
  ): Promise<Json>;
  recoverCrossMonthOpenProjectPayslipsForBatch(
    id: number,
    payload: Json,
  ): Promise<Json>;
  resolveOpenProjectPayslip(id: number, payload: Json): Promise<Json>;
  correctOpenProjectPayslipField(id: number, payload: Json): Promise<Json>;
  confirmOpenProjectPayslip(
    id: number,
    expectedRevision: number,
  ): Promise<Json>;
  viewOpenProjectPayslipPreview(id: number): Promise<PayslipPreviewFile>;
  advanceOpenProjectPayrollWorkflow(
    month: string,
    payload: Json,
  ): Promise<Json>;
  closeOpenProjectPayrollMonth(month: string): Promise<Json>;
  reopenOpenProjectPayrollMonth(month: string, payload: Json): Promise<Json>;
  sendOpenProjectPayrollEmails(
    month: string,
    mode: "dry_run" | "live",
  ): Promise<Json>;
  getOpenProjectPayrollEmailDeliveries(month: string): Promise<Json>;
  getOpenProjectPayrollEmailDeliveryPreview(month: string): Promise<Json>;
  getOpenProjectPohodaExportFile(id: number): Promise<DownloadableFile>;
  getOpenProjectMonthChangeLogFile(id: number): Promise<DownloadableFile>;
  downloadOvertimeReport(fromMonth: string, throughMonth: string): Promise<DownloadResult>;
  downloadOpenProjectPohodaExport(id: number): Promise<DownloadResult>;
  downloadOpenProjectMonthChangeLog(id: number): Promise<DownloadResult>;
  exportOpenProjectPohoda(month: string): Promise<DownloadResult>;
  getOpenProjectTimesheetVersions(
    month: string,
    employeeId: unknown,
  ): Promise<Json>;
  bulkLockOpenProjectTimesheets(
    payload: Json,
    onProgress?: (done: number, total: number) => void,
  ): Promise<BulkLockResult>;
  generateOpenProjectTimesheetVersion(payload: Json): Promise<Json>;
  temporaryUnlockOpenProjectTimesheetVersion(
    month: string,
    employeeId: unknown,
  ): Promise<Json>;
  downloadOpenProjectTimesheetPdf(id: number): Promise<DownloadResult>;
  previewOpenProjectBalanceAdjustment(
    payload: Json,
  ): Promise<OpenProjectBalanceAdjustmentPreviewResponse>;
  createOpenProjectBalanceAdjustment(payload: Json): Promise<Json>;
  acceptOpenProjectBalanceException(payload: Json): Promise<Json>;
  getOpenProjectPayrollValueCorrection(
    employeeId: unknown,
    month: string,
    fieldKey: string,
  ): Promise<OpenProjectPayrollValueCorrectionInspection>;
  mutateOpenProjectPayrollValueCorrection(
    employeeId: unknown,
    month: string,
    fieldKey: string,
    payload: Json,
  ): Promise<OpenProjectPayrollValueCorrectionMutationResponse>;
}

const employeeStableIds = new Map<number, string>();

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function rememberEmployees(
  response: OpenProjectPayrollProfilesResponse,
): OpenProjectPayrollProfilesResponse {
  for (const employee of response?.users ?? []) {
    if (
      Number.isSafeInteger(employee?.privateEmployeeId) &&
      employee.privateEmployeeId > 0 &&
      StableUuidSchema.safeParse(employee.id).success
    )
      employeeStableIds.set(employee.privateEmployeeId, employee.id);
  }
  return response;
}

async function stableEmployeeId(
  value: unknown,
  month = currentMonth(),
): Promise<StableUuid> {
  const stableId = StableUuidSchema.safeParse(value);
  if (stableId.success) return stableId.data;
  // Numeric IDs in TAPP balance/payslip evidence are private Employee.id,
  // never OpenProject user IDs, even when the two numeric ranges overlap.
  const privateId =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value)
        ? Number(value)
        : NaN;
  if (!Number.isSafeInteger(privateId) || privateId <= 0)
    throw new Error(
      "Employee identity is unavailable. Refresh Work Profiles and try again.",
    );
  if (!employeeStableIds.has(privateId)) {
    rememberEmployees(
      await request<OpenProjectPayrollProfilesResponse>(
        `/payroll/legacy-ui/profiles?month=${encodeURIComponent(month)}`,
      ),
    );
  }
  const mapped = employeeStableIds.get(privateId);
  if (!mapped)
    throw new Error(
      "Employee identity is unavailable. Refresh Work Profiles and try again.",
    );
  return mapped as StableUuid;
}

function presentLegacyPeriodSummary(summary: any) {
  return {
    ...summary,
    employees: (summary?.employees ?? []).map((employee: any) => {
      const monthlyRows = Array.isArray(employee.monthlyRows)
        ? employee.monthlyRows
        : Array.isArray(employee.months)
          ? employee.months
          : [];
      const latestResult = [...monthlyRows]
        .filter((row: any) => row?.hasResult === true)
        .sort((left: any, right: any) =>
          String(right.month).localeCompare(String(left.month)),
        )[0];
      return {
        ...employee,
        // The standalone control API deliberately exposes stable employee IDs
        // and calls the per-month collection `months`. The copied TAPP route
        // expects its older `userId` / `monthlyRows` shape.
        userId: employee.userId ?? employee.employeeId,
        monthlyRows: monthlyRows.map((row: any) => ({
          ...row,
          payslipRecordId: row.payslipRecordId ?? row.privatePayslipRecordId,
        })),
        ...(Object.prototype.hasOwnProperty.call(
          employee,
          "remainingVacationCrossCheck",
        )
          ? {}
          : {
              remainingVacationCrossCheck:
                latestResult?.crossChecks?.remainingVacation,
            }),
      };
    }),
  };
}

async function metadata(
  reason = "PayDay payroll operation",
  expectedRevision: number | null = 0,
): Promise<PayrollMetadata> {
  const session = await paydayApi.session();
  return {
    expectedAuthorizationRevision: session.principal.authorizationRevision,
    expectedRevision,
    reason,
    idempotencyKey: crypto.randomUUID(),
  };
}

async function safe<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof ApiRequestError) throw new PayrollApiError(error);
    throw error;
  }
}

async function downloadable(
  operation: Promise<{ blob: Blob; name: string }>,
): Promise<DownloadableFile> {
  const result = await operation;
  return {
    blob: result.blob,
    fileName: result.name,
    contentType: result.blob.type,
  };
}

export const downloadFile = (file: DownloadableFile) => {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return { fileName: file.fileName };
};

export const api: PayrollApi = {
  getOpenProjectPayrollAccess: async () => ({
    user: { id: 0, login: "payday", name: "PayDay user", email: "" },
    officeAccess: true,
    studentStatus: false,
    profile: {},
  }),
  scopedRefresh: async (scope: "payroll" | "work_profiles", reason: string) =>
    safe(async () => {
      const session = await paydayApi.session();
      const result = await paydayApi.scopedRefresh({
        scope,
        reason,
        expectedAuthorizationRevision: session.principal.authorizationRevision,
        idempotencyKey: crypto.randomUUID(),
      });
      const completed = OpenProjectScopedRefreshResultSchema.safeParse(result);
      if (!completed.success)
        throw new Error("Refresh completion could not be verified. Try again.");
      return completed.data;
    }),
  getOpenProjectPayrollProfiles: (month?: string) =>
    safe(async () =>
      rememberEmployees(
        await request<OpenProjectPayrollProfilesResponse>(
          `/payroll/legacy-ui/profiles?month=${encodeURIComponent(month ?? currentMonth())}`,
        ),
      ),
    ),
  updateOpenProjectPayrollProfile: async (id: unknown, payload: Json) =>
    safe(async () => {
      const profiles = rememberEmployees(
        await request<OpenProjectPayrollProfilesResponse>(
          `/payroll/legacy-ui/profiles?month=${encodeURIComponent(currentMonth())}`,
        ),
      );
      const employeeId = await stableEmployeeId(id);
      const current = (profiles.users ?? []).find(
        (candidate: PayrollWorkProfileRow) => candidate.id === employeeId,
      );
      if (!current) throw new Error("Payroll employee was not found.");
      const meta = await metadata(
        String(payload["reason"] ?? "Work profile updated"),
        Number(payload["expectedRevision"] ?? current.revision ?? 0),
      );
      return paydayApi.profile(employeeId, {
        payrollEmployeeCode:
          payload["payrollEmployeeCode"] ?? current.payrollEmployeeCode ?? "",
        payrollEmail: payload["payrollEmail"] ?? current.payrollEmail ?? "",
        ...(payload["payrollPdfPassword"]
          ? { payrollPdfPassword: payload["payrollPdfPassword"] }
          : {}),
        ...(payload["payrollCalculationEnabled"] !== undefined
          ? { calculationEnabled: payload["payrollCalculationEnabled"] }
          : {}),
        ...(payload["payrollContractType"] !== undefined
          ? { contractType: payload["payrollContractType"] }
          : {}),
        ...(payload["payrollWorkloadPercent"] !== undefined
          ? { workloadPercent: payload["payrollWorkloadPercent"] }
          : {}),
        ...(payload["payrollHoursPerDay"] !== undefined
          ? { hoursPerDay: payload["payrollHoursPerDay"] }
          : {}),
        ...(payload["payrollAnnualHolidayEntitlement"] !== undefined
          ? { annualHolidayEntitlement: payload["payrollAnnualHolidayEntitlement"] }
          : {}),
        // Dates are derived from contract history, not contact-edit inputs.
        // Sending them back would materialize derived values into legacy terms
        // and unnecessarily invalidate payroll months.
        notes:
          payload["payrollProfileNotes"] ?? current.payrollProfileNotes ?? "",
        ...meta,
      });
    }),
  saveOpenProjectVacationOpeningBalance: async (id: unknown, payload: Json) =>
    safe(async () =>
      paydayApi.vacation(
        await stableEmployeeId(
          id,
          String(payload["effectiveMonth"] ?? currentMonth()),
        ),
        {
          ...payload,
          ...(await metadata(
            String(payload["reason"] ?? "Vacation balance updated"),
            payload["expectedRevision"] === null
              ? null
              : Number(payload["expectedRevision"] ?? 0),
          )),
        },
      ),
    ),
  getOpenProjectPayrollProfilePeriods: async (id: unknown) =>
    safe(async () => {
      const result: any = await paydayApi.periods(
        await stableEmployeeId(id),
        currentMonth(),
      );
      return {
        periods: (result.periods ?? []).map((period: any) => ({
          ...period,
          id: period.privatePeriodId ?? period.id,
          userId: id,
          validToMonth: period.validToMonth ?? "",
        })),
      };
    }),
  createOpenProjectPayrollProfilePeriod: async (id: unknown, payload: Json) =>
    safe(async () =>
      paydayApi.period(
        await stableEmployeeId(
          id,
          String(payload["validFromMonth"] ?? currentMonth()).slice(0, 7),
        ),
        0,
        "POST",
        {
          ...payload,
          ...(await metadata(
            String(payload["reason"] ?? "Payroll period created"),
            payload["expectedRevision"] === null ||
              payload["expectedRevision"] === undefined
              ? null
              : Number(payload["expectedRevision"] ?? 0),
          )),
        },
      ),
    ),
  updateOpenProjectPayrollProfilePeriod: async (
    id: unknown,
    periodId: number,
    payload: Json,
  ) =>
    safe(async () =>
      paydayApi.period(
        await stableEmployeeId(
          id,
          String(payload["validFromMonth"] ?? currentMonth()).slice(0, 7),
        ),
        periodId,
        "PUT",
        {
          ...payload,
          ...(await metadata(
            String(payload["reason"] ?? "Payroll period updated"),
            payload["expectedRevision"] === null ||
              payload["expectedRevision"] === undefined
              ? null
              : Number(payload["expectedRevision"] ?? 0),
          )),
        },
      ),
    ),
  deleteOpenProjectPayrollProfilePeriod: async (
    id: unknown,
    periodId: number,
    reason: string,
  ) =>
    safe(async () =>
      paydayApi.period(await stableEmployeeId(id), periodId, "DELETE", {
        month: currentMonth(),
        ...(await metadata(reason || "Payroll period deleted")),
      }),
    ),
  getOpenProjectPayrollWorkflow: (month: string) =>
    safe(() => paydayApi.workflow(month)),
  getOpenProjectPayslips: (month: string) =>
    safe(() =>
      request<OpenProjectPayslipList>(
        `/payroll/legacy-ui/payslips?month=${encodeURIComponent(month)}`,
      ),
    ),
  getOpenProjectPayslip: (id: number) =>
    safe(() => request<Json>(`/payroll/legacy-ui/payslips/${String(id)}`)),
  getOpenProjectBalances: (month: string) =>
    /^\d{4}-(0[1-9]|1[0-2])$/.test(month)
      ? safe(() =>
          request<OpenProjectBalanceState>(
            `/payroll/legacy-ui/balances?month=${encodeURIComponent(month)}`,
          ),
        )
      : Promise.reject(new Error("Balances require a valid YYYY-MM month.")),
  getOpenProjectPohodaPreview: (month: string) =>
    safe(
      () =>
        paydayApi.pohoda(month) as unknown as Promise<OpenProjectPohodaPreview>,
    ),
  getOpenProjectPohodaExports: (month: string) =>
    safe(async () => {
      const result: any = await paydayApi.exports(month);
      return {
        month: result.month,
        exports: (result.exports ?? []).map((item: any) => ({
          ...item,
          id: item.privateExportId,
          generatedByUserId: null,
          sourceMetadata: item.sourceMetadata ?? {},
        })),
      };
    }),
  getOpenProjectPayrollAudit: (month?: string | PayrollAuditQuery) =>
    safe(async () => {
      const query: Parameters<typeof paydayApi.audit>[0] =
        typeof month === "string"
          ? { month }
          : {
              level:
                month?.level ??
                (month?.technical === true ? "technical" : "business"),
              ...(month?.month !== undefined ? { month: month.month } : {}),
              ...(month?.userId !== undefined
                ? {
                    userId: await stableEmployeeId(
                      month?.userId,
                      month?.month ??
                        month?.throughMonth ??
                        month?.fromMonth ??
                        currentMonth(),
                    ),
                  }
                : {}),
              ...(month?.fromMonth !== undefined
                ? { fromMonth: month.fromMonth }
                : {}),
              ...(month?.throughMonth !== undefined
                ? { throughMonth: month.throughMonth }
                : {}),
              ...(month?.fieldKey !== undefined
                ? { fieldKey: month.fieldKey }
                : {}),
              ...(month?.eventKind !== undefined
                ? { eventKind: month.eventKind }
                : {}),
              ...(month?.scope !== undefined ? { scope: month.scope } : {}),
            };
      return paydayApi.audit(
        query,
      ) as unknown as Promise<OpenProjectPayrollAuditResponse>;
    }),
  getOpenProjectMonthChangeLogs: (month: string) =>
    safe(async () => {
      const result: any = await paydayApi.logs(month);
      return {
        ...result,
        versions: (result.versions ?? []).map((item: any) => ({
          ...item,
          id: item.privateVersionId,
        })),
      };
    }),
  getOpenProjectPeriodSummary: (fromMonth: string, throughMonth: string) =>
    safe(async () =>
      presentLegacyPeriodSummary(
        await paydayApi.summary(fromMonth, throughMonth),
      ),
    ),
  getOpenProjectYearSummary: (year: number, throughMonth: string) =>
    safe(async () =>
      presentLegacyPeriodSummary(
        await paydayApi.yearSummary(year, throughMonth),
      ),
    ),
  uploadOpenProjectPayslips: async (month: string, files: File[]) =>
    safe(async () => {
      const form = new FormData();
      form.set("month", month);
      const meta = await metadata("Payslip PDFs uploaded");
      for (const [key, value] of Object.entries(meta))
        form.set(key, String(value));
      for (const file of files) form.append("files", file);
      return paydayApi.uploadPayslips(form);
    }),
  calculateOpenProjectBalanceRow: async (month: string, employeeId: string, expectedRunId: number, expectedRunRevision: number) =>
    safe(async () => paydayApi.calculateRow({ month, employeeId, expectedRunId, expectedRunRevision,
      ...(await metadata("Employee payroll balance recalculated")),
    })),
  calculateOpenProjectBalances: async (month: string) =>
    safe(async () =>
      paydayApi.calculate({
        month,
        ...(await metadata("Payroll balances calculated")),
      }),
    ),
  approveOpenProjectBalanceRun: async (
    id: number,
    revision: number,
    reason = "",
  ) =>
    safe(async () =>
      paydayApi.approve(
        id,
        await metadata(reason || "Payroll balances approved", revision),
      ),
    ),
  confirmReadyOpenProjectPayslips: async (month: string, reason = "") =>
    safe(async () =>
      paydayApi.confirmReadyPayslips({
        month,
        ...(await metadata(reason || "Ready payslips confirmed")),
      }),
    ),
  deleteOpenProjectPayslip: async (
    id: number,
    reason = "",
    expectedRevision = 0,
    wrongMonthCleanup?: string,
  ) =>
    safe(async () =>
      paydayApi.deletePayslip(id, {
        ...(await metadata(reason || "Payslip deleted", expectedRevision)),
        ...(wrongMonthCleanup ? { wrongMonthCleanup } : {}),
      }),
    ),
  keepExistingOpenProjectPayslipsForBatch: async (id: number, payload: Json) =>
    safe(async () =>
      paydayApi.batch(id, "keep-existing", {
        ...payload,
        ...(await metadata(
          String(payload["reason"] ?? "Existing payslips kept"),
        )),
      }),
    ),
  recoverCrossMonthOpenProjectPayslipsForBatch: async (
    id: number,
    payload: Json,
  ) =>
    safe(async () =>
      paydayApi.batch(id, "recover-cross-month", {
        ...payload,
        ...(await metadata(
          String(payload["reason"] ?? "Cross-month payslips recovered"),
        )),
      }),
    ),
  resolveOpenProjectPayslip: async (id: number, payload: Json) =>
    safe(async () =>
      paydayApi.payslipAction(id, "resolve", "PATCH", {
        ...payload,
        employeeId: await stableEmployeeId(
          payload["employeeId"],
          String(payload["confirmedPeriod"] ?? currentMonth()),
        ),
        ...(await metadata(String(payload["reason"] ?? "Payslip resolved"))),
      }),
    ),
  correctOpenProjectPayslipField: async (id: number, payload: Json) =>
    safe(async () =>
      paydayApi.payslipAction(id, "fields", "PATCH", {
        ...payload,
        ...(await metadata(
          String(payload["reason"] ?? "Payslip field corrected"),
          Number(payload["expectedRevision"] ?? 0),
        )),
      }),
    ),
  confirmOpenProjectPayslip: async (id: number, expectedRevision: number) =>
    safe(async () =>
      paydayApi.payslipAction(
        id,
        "confirm",
        "POST",
        await metadata("Payslip record confirmed", expectedRevision),
      ),
    ),
  viewOpenProjectPayslipPreview: async (id: number) =>
    safe(async () => {
      const file = await paydayApi.payslipPreview(id);
      // Keep the blob in the query cache. A viewer creates and owns its own
      // object URL, so closing one viewer cannot revoke the URL reused by the
      // next rapid View click.
      return { fileName: file.name, blob: file.blob };
    }),
  advanceOpenProjectPayrollWorkflow: async (month: string, payload: Json) =>
    safe(async () =>
      paydayApi.workflowMutate(month, {
        action: "advance",
        fromStage: payload["fromStage"],
        ...(await metadata(
          "Payroll workflow advanced",
          Number(payload["expectedVersion"] ?? 0),
        )),
      }),
    ),
  closeOpenProjectPayrollMonth: async (month: string) =>
    safe(async () =>
      paydayApi.workflowMutate(month, {
        action: "close",
        ...(await metadata("Payroll month closed")),
      }),
    ),
  reopenOpenProjectPayrollMonth: async (month: string, payload: Json) =>
    safe(async () =>
      paydayApi.workflowMutate(month, {
        action: "reopen",
        fromStage: payload["fromStage"],
        ...(await metadata(
          String(payload["reason"] ?? "Payroll month reopened"),
        )),
      }),
    ),
  sendOpenProjectPayrollEmails: async (
    month: string,
    mode: "dry_run" | "live",
  ) => safe(async () => paydayApi.sendPayrollEmails(month, mode)),
  getOpenProjectPayrollEmailDeliveries: async (month: string) =>
    safe(async () => paydayApi.emailDeliveries(month)),
  getOpenProjectPayrollEmailDeliveryPreview: async (month: string) =>
    safe(async () => paydayApi.emailDeliveryPreview(month)),
  downloadOvertimeReport: async (fromMonth: string, throughMonth: string) =>
    downloadFile(await downloadable(paydayApi.overtimeReport(fromMonth, throughMonth))),
  getOpenProjectPohodaExportFile: (id: number) =>
    downloadable(paydayApi.pohodaXlsx(id)),
  getOpenProjectMonthChangeLogFile: (id: number) =>
    downloadable(paydayApi.monthLogPdf(id)),
  downloadOpenProjectPohodaExport: async (id: number) =>
    downloadFile(await downloadable(paydayApi.pohodaXlsx(id))),
  downloadOpenProjectMonthChangeLog: async (id: number) =>
    downloadFile(await downloadable(paydayApi.monthLogPdf(id))),
  exportOpenProjectPohoda: async (month: string) => {
    const file = await downloadable(
      paydayApi.createPohoda({
        month,
        ...(await metadata("POHODA payroll workbook exported")),
      }),
    );
    return downloadFile(file);
  },
  getOpenProjectTimesheetVersions: async (month: string, employeeId: unknown) =>
    safe(async () =>
      paydayApi.timesheetVersions(
        await stableEmployeeId(employeeId, month),
        month,
      ),
    ),
  bulkLockOpenProjectTimesheets: async (
    payload: Json,
    onProgress?: (done: number, total: number) => void,
  ) =>
    safe(async () => {
      const month = String(payload["month"] ?? currentMonth());
      const profiles = rememberEmployees(
        await request<OpenProjectPayrollProfilesResponse>(
          `/payroll/legacy-ui/profiles?month=${encodeURIComponent(month)}`,
        ),
      );
      const employees = (profiles.users ?? []).filter(
        (employee: PayrollWorkProfileRow) =>
          (payload["unlock"] ||
            (employee.payrollCalculationEnabled !== false &&
              employee.payrollEligibleForMonth !== false)) &&
          (!payload["employeeIds"] ||
            (payload["employeeIds"] as number[]).includes(
              employee.privateEmployeeId,
            )),
      );
      const results: Array<{
        userId: number | string;
        name: string;
        locked: boolean;
        skipped: boolean;
        error: string;
      }> = [];
      for (const employee of employees) {
        const employeeId = await stableEmployeeId(employee.id, month);
        const userId = employee.privateEmployeeId;
        const name =
          employee.displayName || employee.username || String(userId);
        try {
          const state: any = await paydayApi.timesheetLock(employeeId, month);
          if (payload["unlock"] ? !state.locked : state.locked) {
            results.push({
              userId,
              name,
              locked: Boolean(state.locked),
              skipped: true,
              error: "",
            });
            continue;
          }
          if (!payload["unlock"] && !state.available)
            throw new Error(
              "This employee is not available for a fresh timesheet lock.",
            );
          if (payload["unlock"])
            await paydayApi.releaseTimesheet(
              employeeId,
              month,
              await metadata(
                String(payload["reason"]),
                Number(state.revision ?? 0),
              ),
            );
          else
            await paydayApi.lockTimesheet({
              employeeId,
              month,
              ...(state.mirrorRunId ? { mirrorRunId: state.mirrorRunId } : {}),
              ...(await metadata(
                String(payload["reason"] ?? "Payroll timesheets bulk locked"),
                Number(state.revision ?? 0),
              )),
            });
          results.push({
            userId,
            name,
            locked: !payload["unlock"],
            skipped: false,
            error: "",
          });
        } catch (error) {
          results.push({
            userId,
            name,
            locked: false,
            skipped: false,
            error:
              error instanceof Error
                ? error.message
                : "Employee action failed.",
          });
        } finally {
          onProgress?.(results.length, employees.length);
        }
      }
      return {
        month,
        unlocked: payload["unlock"]
          ? results.filter((result) => !result.skipped && !result.error).length
          : 0,
        attempted: employees.length,
        locked: results.filter((result) => result.locked && !result.skipped)
          .length,
        skipped: results.filter((result) => result.skipped).length,
        failed: results.filter((result) => result.error).length,
        results,
      };
    }),
  generateOpenProjectTimesheetVersion: async (payload: Json) =>
    safe(async () => {
      const month = String(payload["month"] ?? currentMonth());
      const employeeId = await stableEmployeeId(payload["employeeId"], month);
      const state: any = await paydayApi.timesheetLock(employeeId, month);
      if (state.locked) return { locked: true, disposition: "already_locked" };
      if (!state.available)
        throw new Error(
          "This employee is not available for a fresh timesheet lock.",
        );
      return paydayApi.lockTimesheet({
        employeeId,
        month,
        ...(state.mirrorRunId ? { mirrorRunId: state.mirrorRunId } : {}),
        ...(await metadata(
          String(payload["reason"] ?? "Payroll timesheet locked"),
          Number(state.revision ?? 0),
        )),
      });
    }),
  temporaryUnlockOpenProjectTimesheetVersion: async (
    month: string,
    employeeId: unknown,
  ) =>
    safe(async () =>
      paydayApi.releaseTimesheet(
        await stableEmployeeId(employeeId, month),
        month,
        await metadata(
          "Payroll timesheet temporarily unlocked",
          Number(
            (
              await paydayApi.timesheetLock(
                await stableEmployeeId(employeeId, month),
                month,
              )
            )["revision"] ?? 0,
          ),
        ),
      ),
    ),
  downloadOpenProjectTimesheetPdf: async (id: number) =>
    safe(async () =>
      downloadFile(await downloadable(paydayApi.timesheetPdf(id))),
    ),
  previewOpenProjectBalanceAdjustment: async (payload: Json) =>
    safe(
      async () =>
        paydayApi.adjustmentPreview({
          employeeId: await stableEmployeeId(
            payload["userId"] ?? payload["employeeId"],
            String(payload["effectiveMonth"] ?? currentMonth()),
          ),
          balanceType: payload["balanceType"],
          inputAmount: payload["inputAmount"],
          inputUnit: payload["inputUnit"],
          effectiveMonth: payload["effectiveMonth"],
        }) as unknown as Promise<OpenProjectBalanceAdjustmentPreviewResponse>,
    ),
  createOpenProjectBalanceAdjustment: async (payload: Json) =>
    safe(async () =>
      paydayApi.adjustment({
        employeeId: await stableEmployeeId(
          payload["userId"] ?? payload["employeeId"],
          String(payload["effectiveMonth"] ?? currentMonth()),
        ),
        balanceType: payload["balanceType"],
        adjustmentType: payload["adjustmentType"],
        inputAmount: payload["inputAmount"],
        inputUnit: payload["inputUnit"],
        effectiveMonth: payload["effectiveMonth"],
        conversionBasis: payload["conversionBasis"],
        ...(await metadata(
          String(payload["reason"] ?? "Balance adjusted"),
          Number(payload["expectedRevision"] ?? 0),
        )),
        ...(payload["requestId"]
          ? { idempotencyKey: payload["requestId"] }
          : {}),
      }),
    ),
  acceptOpenProjectBalanceException: async (payload: Json) =>
    safe(async () =>
      paydayApi.exception({
        privateResultId: payload["privateResultId"] ?? payload["resultId"],
        checkKey: payload["checkKey"],
        resolutionAction: payload["resolutionAction"],
        ...(await metadata(
          String(payload["reason"] ?? "Payroll difference accepted"),
          Number(payload["expectedRevision"] ?? 0),
        )),
        ...(payload["requestId"]
          ? { idempotencyKey: payload["requestId"] }
          : {}),
      }),
    ),
  getOpenProjectPayrollValueCorrection: async (
    employeeId: unknown,
    month: string,
    fieldKey: string,
  ) =>
    safe(
      async () =>
        paydayApi.correction(
          await stableEmployeeId(employeeId, month),
          month,
          fieldKey,
        ) as unknown as Promise<OpenProjectPayrollValueCorrectionInspection>,
    ),
  mutateOpenProjectPayrollValueCorrection: async (
    employeeId: unknown,
    month: string,
    fieldKey: string,
    payload: Json,
  ) =>
    safe(
      async () =>
        paydayApi.correct(
          await stableEmployeeId(employeeId, month),
          month,
          fieldKey,
          {
            ...payload,
            ...(await metadata(
              String(
                payload["note"] ??
                  payload["reason"] ??
                  "Payroll value corrected",
              ),
              Number(payload["expectedRevision"] ?? 0),
            )),
          },
        ) as unknown as Promise<OpenProjectPayrollValueCorrectionMutationResponse>,
    ),
};
